import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const LANGS = ['EN', 'FR', 'NL'];

export default function Header() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  const nav = [
    { to: '/who-we-are', label: t.nav.who },
    { to: '/new-projects', label: t.nav.new_projects },
    { to: '/for-sale', label: t.nav.for_sale },
    { to: '/for-rent', label: t.nav.for_rent },
    { to: '/management', label: t.nav.management },
    { to: '/contact', label: t.nav.contact }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur shadow-sm' : 'bg-white/80 backdrop-blur-sm'
      }`}
    >
      <div className="max-w-[1440px] mx-auto flex items-center justify-between px-4 md:px-8 h-20">
        <Link to="/" className="flex items-center" aria-label="Imodeff home">
          <img
            src="/assets/imodeff-logo.png"
            alt="Imodeff Promotion"
            className="h-14 md:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `text-[12px] xl:text-[13px] tracking-[0.12em] xl:tracking-[0.15em] font-medium transition-colors hover:text-[#e11f3a] whitespace-nowrap ${
                  isActive ? 'text-[#e11f3a]' : 'text-black'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setLangOpen((v) => !v)}
              className="flex items-center gap-1 border border-black/70 px-3 py-1.5 text-[13px] tracking-widest hover:bg-black hover:text-white transition-colors"
            >
              {lang}
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
            {langOpen && (
              <div className="absolute right-0 mt-1 bg-white border border-black/20 shadow-lg min-w-[70px] z-50">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    onClick={() => {
                      setLang(l);
                      setLangOpen(false);
                    }}
                    className={`block w-full text-left px-3 py-2 text-[13px] tracking-widest hover:bg-neutral-100 ${
                      l === lang ? 'text-[#e11f3a]' : 'text-black'
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            className="lg:hidden p-2 text-black"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-black/10">
          <nav className="flex flex-col px-6 py-4 gap-1">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `py-3 text-[13px] tracking-[0.15em] font-medium border-b border-black/5 ${
                    isActive ? 'text-[#e11f3a]' : 'text-black'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
